import React, { Ref, forwardRef } from 'react';

import { IconButton, InputAdornment, TextField } from '@mui/material';
import { BaseTextFieldProps } from '@mui/material/TextField/TextField';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';

import clsx from 'clsx';
import { Property } from 'csstype';

import ErrorTooltipIcon from '../ErrorTooltipIcon';
import { useStyles } from './AppTextField.styles';

export interface IAppTextFieldProps extends BaseTextFieldProps {
  onChange?: (value: any) => void;
  InputProps?: any;
  withClearButton?: boolean;
  textAlign?: Property.TextAlign | undefined;
  fullWidth?: boolean;
  errorIconRightPadding?: number; // todo think how to do it better
  mode?: 'light' | 'dark';
}

const AppTextField = forwardRef(function AppTextField(
  props: IAppTextFieldProps,
  ref?: Ref<HTMLDivElement>,
) {
  const {
    withClearButton = false,
    inputProps = {},
    value,
    textAlign,
    error,
    helperText,
    inputRef,
    fullWidth = false,
    mode = 'light',
    InputProps,
    errorIconRightPadding,
    ...textFieldProps
  } = props;
  const { classes } = useStyles({ fullWidth, mode });
  const hasLabel = Boolean(textFieldProps.label);
  const inputLabelProps = hasLabel
    ? {
        ...(textFieldProps.InputLabelProps || {}),
        shrink: true,
        className: clsx(
          textFieldProps.InputLabelProps?.className,
          classes.label,
        ),
      }
    : textFieldProps.InputLabelProps;

  function renderEndAdornment() {
    const clearButton = withClearButton && String(value).length > 0 && (
      <IconButton
        id="close-icon"
        className={classes.closeIcon}
        aria-label="remove value"
        onClick={() => {
          props.onChange && props.onChange({ target: { value: '' } });
        }}
        size="large">
        <CloseIcon />
      </IconButton>
    );

    const errorIcon = error && <ErrorTooltipIcon helperText={helperText} />;

    return (
      <>
        {InputProps?.endAdornment}
        <InputAdornment
          position="end"
          style={{ right: errorIconRightPadding || 10, position: 'absolute' }}>
          {clearButton}
          {errorIcon}
        </InputAdornment>
      </>
    );
  }

  return (
    <TextField
      ref={ref}
      inputRef={inputRef}
      color="primary"
      classes={{ root: classes.root, ...(props.classes || {}) }}
      value={value}
      InputLabelProps={inputLabelProps}
      InputProps={{
        ...(InputProps || {}),
        ...(hasLabel ? { notched: false } : {}),
        endAdornment:
          withClearButton || error || InputProps?.endAdornment
            ? renderEndAdornment()
            : undefined,
      }}
      inputProps={{
        style: { textAlign },
        ...inputProps,
      }}
      error={error}
      {...textFieldProps}
    />
  );
});

export default AppTextField;
